import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { buildPrincipalCustomerFields } from '../customers/principalCustomer';
import { ICustomerType } from '../../constants/customer';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useFetchCustomers } from '../../hooks/useFetchCustomers';
import { ICustomer } from '../../interface/customer';
import { IMatterRequest } from '../../interface/matterRequest';

const customerTypeLabelKeys: Record<ICustomerType, string> = {
  [ICustomerType.COMPANY]: 'customer:customerType.company',
  [ICustomerType.PRIVATE_PERSON]: 'customer:customerType.privatePerson',
};

/**
 * The props `SearchableSelect` needs to pick the existing customer of a matter request, from the
 * same list the customer picker screen uses. The picker's favourites and type filter stay there.
 */
export const useMatterRequestCustomerSelect = () => {
  const { t } = useTranslation();
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: customers,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchCustomers({
    sortBy: ['name:ASC'],
    limit: 50,
    search: searchValue,
    fields: ['name', 'type'],
  });

  const options: SelectOption[] = customers.map((customer: ICustomer) => ({
    value: customer.id,
    label: customer.name,
    description: t(customerTypeLabelKeys[customer.type]),
  }));

  const toValue = (): SelectOption | null => {
    if (matterRequest?.principalCompanyId) {
      return {
        value: matterRequest.principalCompanyId,
        label: matterRequest.principalCompany?.name ?? '',
        description: t(customerTypeLabelKeys[ICustomerType.COMPANY]),
      };
    }
    if (matterRequest?.principalPrivatePersonId) {
      return {
        value: matterRequest.principalPrivatePersonId,
        label: matterRequest.principalPrivatePerson?.fullName ?? '',
        description: t(customerTypeLabelKeys[ICustomerType.PRIVATE_PERSON]),
      };
    }
    return null;
  };

  const onChange = (option: SelectOption) => {
    const customer = customers.find(({ id }: ICustomer) => id === option.value);
    if (!customer) return;
    const principalFields = buildPrincipalCustomerFields(customer);
    if (!principalFields) return;
    updateManageMatterRequest({
      ...matterRequest,
      ...principalFields,
    } as unknown as IMatterRequest);
  };

  return {
    options,
    value: toValue(),
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
